import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { PRODUCTS as LOCAL } from '../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ProductsContext = createContext();
export const useProducts = () => useContext(ProductsContext);

const FRAGRANCE_DESC = {
  agrumi: 'Fresco, vivace, energizzante',
  bergamotto: 'Agrumato, frizzante, pulito',
  lavanda: 'Rilassante, floreale, avvolgente',
  'fiori-bianchi': 'Delicato, fresco, luminoso',
  gelsomino: 'Floreale, elegante, persistente',
  giglio: 'Raffinato, intenso, elegante',
  'fiori-di-loto': 'Sofisticato, sereno, delicato',
  orchidea: 'Floreale, sofisticato, avvolgente',
};

// Map Shopify professional handle -> local content slug
const PRO_HANDLE_TO_LOCAL = {
  superpulitore: 'superpulitore-igienizzante-professionale',
  'sgrassatore-strong': 'strong-sgrassatore-smacchiatore-professionale',
  'vetri-specchi-e-cristalli': 'vetri-specchi-cristalli-professionale',
  'detersivo-lavatrice': 'detersivo-lavatrice-professionale',
};

const normFrag = (f) => (f === 'fiori-di-loto' ? 'loto' : f);

function findLocal(sp) {
  if (sp.category === 'professional') return LOCAL.find(l => l.slug === PRO_HANDLE_TO_LOCAL[sp.id]);
  if (sp.category === 'bundle') return LOCAL.find(l => l.id === 'p15');
  return LOCAL.find(l => l.category === sp.category && normFrag(l.fragrance) === normFrag(sp.fragrance));
}

// Merge premium Emergent content onto Shopify product (price/availability/variant stay from Shopify)
function mergeContent(sp) {
  const loc = findLocal(sp);
  if (!loc) return sp;
  return {
    ...sp,
    name: loc.name,
    subtitle: loc.subtitle,
    short: loc.short,
    long: loc.long,
    benefits: loc.benefits,
    usage: loc.usage,
    sensation: loc.sensation,
    intensity: loc.intensity,
    fragranceLabel: loc.fragranceLabel,
    images: (loc.images && loc.images.length) ? loc.images : sp.images,
    pro: loc.pro,
  };
}

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/catalog/products`)
      .then(r => { if (mounted) setProducts(r.data.products || []); })
      .catch(e => { if (mounted) setError(e); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const getProduct = (slug) => products.find(p => p.slug === slug || p.id === slug);
  const byCategory = (cat) => products.filter(p => p.category === cat);

  const fragrances = useMemo(() => {
    const seen = {};
    for (const p of products) {
      if (p.fragrance && p.fragrance !== 'mix' && !seen[p.fragrance]) {
        seen[p.fragrance] = { slug: p.fragrance, name: p.fragranceLabel, desc: FRAGRANCE_DESC[p.fragrance] || '', image: p.images?.[0] };
      }
    }
    return Object.values(seen);
  }, [products]);

  return (
    <ProductsContext.Provider value={{ products, loading, error, getProduct, byCategory, fragrances }}>
      {children}
    </ProductsContext.Provider>
  );
}
